import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { useCatalogo, classesDaCategoria, type TipoDeManutencao } from '@/lib/catalogo';
import {
  lerManutencoes,
  urlDoDocumento,
  type AnexoDeDocumento,
  type Manutencao,
} from '@/lib/condominioRepo';

type InfraItem = {
  id: string;
  condominio_id: string;
  nome: string;
  categoria: string;
  status: string;
  localizacao?: string;
  ultima_manutencao?: string;
  proxima_manutencao?: string;
  responsavel?: string;
  capacidade?: string;
  observacoes?: string;
  recorrencia?: { ativa: boolean; frequencia: string };
};

export default function InfraItemDetailPage() {
  const { id: condoId, itemId } = useParams<{ id: string; itemId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [condoNome, setCondoNome] = useState('');
  const [item, setItem] = useState<InfraItem | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'historico'>('historico');
  const [prestadorNome, setPrestadorNome] = useState<string | null>(null);

  useEffect(() => {
    if (!condoId || !itemId) return;
    (async () => {
      const [condoRes, infraRes] = await Promise.all([
        supabase.from('condominios').select('nome').eq('id', condoId).single(),
        supabase.from('infraestrutura').select('*').eq('id', itemId).single(),
      ]);

      if (condoRes.error || infraRes.error) {
        setError('Erro ao carregar dados.');
        setLoading(false);
        return;
      }
      setCondoNome(condoRes.data?.nome || '');
      const infraData = infraRes.data as InfraItem;
      setItem(infraData);

      if ((infraData as any).prestador_id) {
        const { data: prestData } = await supabase
          .from('prestadores')
          .select('nome')
          .eq('id', (infraData as any).prestador_id)
          .maybeSingle();
        if (prestData) setPrestadorNome(prestData.nome);
      }

      setLoading(false);
    })();
  }, [condoId, itemId]);

  // A timeline lia `infraestrutura.historico`, um jsonb que nunca recebeu uma
  // linha — por isso mostrava "nenhum registro" para sempre. Agora vem da tabela
  // `manutencoes` (migration 0031), a mesma que a aba Manutenções alimenta.
  const [manutencoes, setManutencoes] = useState<Manutencao[]>([]);
  const { itens: tiposDeManutencao } = useCatalogo<TipoDeManutencao>('manutencao_tipos');
  const tipoPorId = Object.fromEntries(tiposDeManutencao.map((t) => [t.id, t]));

  // Bucket privado: a URL e assinada na hora do clique, e nao guardada — uma
  // URL assinada guardada e um link que expira sem avisar.
  const abrirAnexo = async (anexo: AnexoDeDocumento) => {
    const url = await urlDoDocumento('manutencoes', anexo.caminho);
    if (url) window.open(url, '_blank', 'noopener');
  };

  useEffect(() => {
    if (!condoId || !itemId) return;
    let cancelado = false;
    lerManutencoes(condoId, itemId)
      .then((registros) => {
        if (!cancelado) setManutencoes(registros);
      })
      .catch(() => {
        if (!cancelado) setManutencoes([]);
      });
    return () => {
      cancelado = true;
    };
  }, [condoId, itemId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto py-12 text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <p className="text-base font-semibold text-foreground-800">
            {error || 'Item não encontrado'}
          </p>
          <p className="text-sm text-foreground-500 mt-1 mb-6">
            {error ? '' : 'O item de infraestrutura solicitado não existe ou foi removido.'}
          </p>
          <Link
            to={`/condominios/${condoId}/infraestrutura`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-s-line"></i>Voltar para Infraestrutura
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const formatData = (iso: string) => {
    if (!iso) return '-';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'Operacional':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'Em Manutenção':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Desativado':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-background-100 text-foreground-700 border-background-300';
    }
  };

  const categoriaBadge = (cat: string) => {
    const map: Record<string, string> = {
      Lazer: 'bg-accent-100 text-accent-900 border-accent-300',
      Segurança: 'bg-secondary-100 text-secondary-900 border-secondary-200',
      Mobilidade: 'bg-primary-50 text-primary-700 border-primary-200',
      Serviços: 'bg-secondary-100 text-secondary-900 border-secondary-200',
      Sustentabilidade: 'bg-green-50 text-green-700 border-green-200',
    };
    return map[cat] || 'bg-background-100 text-foreground-700 border-background-300';
  };

  const categoriaIcon = (cat: string) => {
    const map: Record<string, string> = {
      Lazer: 'ri-football-line',
      Segurança: 'ri-shield-check-line',
      Mobilidade: 'ri-arrow-up-down-line',
      Serviços: 'ri-tools-line',
      Sustentabilidade: 'ri-leaf-line',
    };
    return map[cat] || 'ri-building-line';
  };

  const frequenciaLabel: Record<string, string> = {
    semanal: 'Semanal',
    quinzenal: 'Quinzenal',
    mensal: 'Mensal',
    bimestral: 'Bimestral',
    trimestral: 'Trimestral',
    semestral: 'Semestral',
    anual: 'Anual',
  };

  const formatSize = (bytes: number) => {
    const kb = bytes / 1024;
    return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
  };

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue =
    item.proxima_manutencao && item.proxima_manutencao < today && item.status !== 'Desativado';
  const isApproaching = (() => {
    if (!item.proxima_manutencao || item.status === 'Desativado' || isOverdue) return false;
    const next = new Date(item.proxima_manutencao);
    const diff = Math.ceil((next.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return diff >= 0 && diff <= 15;
  })();

  return (
    <DashboardLayout>
      <div className="max-w-5xl">
        {/* breadcrumb */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Link
              to="/condominios"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>
              Condomínios
            </Link>
            <Link
              to={`/condominios/${condoId}/infraestrutura`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>
              Infraestrutura
            </Link>
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded flex items-center justify-center ${categoriaBadge(item.categoria).split(' ')[0]}`}
              >
                <i
                  className={`${categoriaIcon(item.categoria)} text-xs ${categoriaBadge(item.categoria).split(' ')[1]}`}
                ></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900">{item.nome}</h1>
            </div>
          </div>
        </div>

        {/* status alerts */}
        {(isOverdue || isApproaching) && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg border flex items-start gap-3 ${
              isOverdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                isOverdue ? 'bg-red-100' : 'bg-amber-100'
              }`}
            >
              <i
                className={`text-xs ${isOverdue ? 'ri-error-warning-line text-red-600' : 'ri-timer-line text-amber-600'}`}
              ></i>
            </div>
            <div>
              <p
                className={`text-sm font-semibold ${isOverdue ? 'text-red-800' : 'text-amber-800'}`}
              >
                {isOverdue ? 'Manutenção Atrasada!' : 'Manutenção Próxima'}
              </p>
              <p className={`text-sm mt-0.5 ${isOverdue ? 'text-red-700' : 'text-amber-700'}`}>
                {isOverdue
                  ? `A manutenção estava prevista para ${formatData(item.proxima_manutencao || '')}. Agende uma nova data o quanto antes.`
                  : `A próxima manutenção está agendada para ${formatData(item.proxima_manutencao || '')}. Planeje os recursos necessários.`}
              </p>
            </div>
          </div>
        )}

        {/* tabs */}
        <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1 w-fit mb-6">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
              activeTab === 'info'
                ? 'bg-background-50 text-foreground-900'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-information-line mr-1.5"></i>Informações
          </button>
          <button
            onClick={() => setActiveTab('historico')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
              activeTab === 'historico'
                ? 'bg-background-50 text-foreground-900'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-history-line mr-1.5"></i>Histórico
            {manutencoes.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] bg-background-100 text-foreground-600">
                {manutencoes.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'info' ? (
          <div className="flex flex-col gap-6">
            {/* header card */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${categoriaBadge(item.categoria).split(' ')[0]}`}
                  >
                    <i
                      className={`${categoriaIcon(item.categoria)} text-lg ${categoriaBadge(item.categoria).split(' ')[1]}`}
                    ></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-foreground-900">{item.nome}</h2>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${categoriaBadge(item.categoria)}`}
                      >
                        {item.categoria}
                      </span>
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadge(item.status)}`}
                      >
                        {item.status === 'Em Manutenção' ? 'Em Manutenção' : item.status}
                      </span>
                      {item.recorrencia?.ativa && (
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border bg-background-100 text-foreground-700 border-background-300">
                          <i className="ri-refresh-line mr-1"></i>
                          Recorrente (
                          {frequenciaLabel[item.recorrencia.frequencia] ||
                            item.recorrencia.frequencia}
                          )
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Link
                  to={`/condominios/${condoId}/infraestrutura`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-pencil-line"></i>
                  Editar
                </Link>
              </div>
            </div>

            {/* info grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* dados gerais */}
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">Dados Gerais</h3>
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                    <span className="text-sm text-foreground-500">Localização</span>
                    <span className="text-sm font-medium text-foreground-800 text-right max-w-[60%]">
                      {item.localizacao}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                    <span className="text-sm text-foreground-500">Responsável</span>
                    <span className="text-sm font-medium text-foreground-800 text-right max-w-[60%]">
                      {item.responsavel || '-'}
                    </span>
                  </div>
                  {prestadorNome && (
                    <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                      <span className="text-sm text-foreground-500">Prestador vinculado</span>
                      <span className="text-sm font-medium text-foreground-800 text-right max-w-[60%] flex items-center gap-1.5">
                        <div className="w-4 h-4 flex items-center justify-center text-foreground-400">
                          <i className="ri-building-2-line text-[10px]"></i>
                        </div>
                        {prestadorNome}
                      </span>
                    </div>
                  )}
                  {item.capacidade && item.capacidade !== '-' && (
                    <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                      <span className="text-sm text-foreground-500">Capacidade</span>
                      <span className="text-sm font-medium text-foreground-800">
                        {item.capacidade}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                    <span className="text-sm text-foreground-500">Categoria</span>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${categoriaBadge(item.categoria)}`}
                    >
                      {item.categoria}
                    </span>
                  </div>
                </div>
              </div>

              {/* manutenção */}
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">Manutenção</h3>
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                    <span className="text-sm text-foreground-500">Última manutenção</span>
                    <span className="text-sm font-medium text-foreground-800">
                      {formatData(item.ultima_manutencao || '')}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                    <span className="text-sm text-foreground-500">Próxima manutenção</span>
                    <span
                      className={`text-sm font-medium ${isOverdue ? 'text-red-600' : isApproaching ? 'text-amber-600' : 'text-foreground-800'}`}
                    >
                      {formatData(item.proxima_manutencao || '')}
                      {isOverdue && ' (Atrasada)'}
                      {isApproaching && ' (Em breve)'}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                    <span className="text-sm text-foreground-500">Status</span>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(item.status)}`}
                    >
                      {item.status === 'Em Manutenção' ? 'Em Manutenção' : item.status}
                    </span>
                  </div>
                  {item.recorrencia?.ativa && (
                    <div className="flex justify-between items-start py-2 border-b border-background-200/50">
                      <span className="text-sm text-foreground-500">Recorrência</span>
                      <span className="text-sm font-medium text-foreground-800 capitalize">
                        {frequenciaLabel[item.recorrencia.frequencia]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* observações */}
            {item.observacoes && (
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">Observações</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{item.observacoes}</p>
              </div>
            )}
          </div>
        ) : (
          /* histórico */
          <div className="bg-background-50 border border-background-200/70 rounded-lg">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">
                Histórico de Manutenções
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500">
                  {manutencoes.length} registro(s)
                </span>
                <Link
                  to={`/condominios/${condoId}/manutencoes`}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <i className="ri-add-line"></i>Registrar
                </Link>
              </div>
            </div>

            {manutencoes.length === 0 ? (
              <div className="p-10 text-center">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-history-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-sm font-medium text-foreground-700">
                  Nenhuma manutenção registrada
                </p>
                <p className="text-sm text-foreground-500 mt-1">
                  O primeiro registro define a data da última manutenção deste item.
                </p>
              </div>
            ) : (
              <div className="p-5 md:p-6">
                <div className="relative">
                  {/* linha da timeline */}
                  <div className="absolute left-[17px] top-2 bottom-2 w-px bg-background-200/70"></div>

                  <div className="flex flex-col gap-4">
                    {manutencoes.map((registro) => {
                      const tipo = tipoPorId[registro.tipoId];
                      return (
                        <div key={registro.id} className="flex items-start gap-4 relative">
                          <div
                            className={`relative z-10 mt-1.5 w-[11px] h-[11px] rounded-full border-2 flex-shrink-0 ${classesDaCategoria(tipo?.cor)}`}
                          ></div>
                          <div className="flex-1 min-w-0 bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-background-300 transition-colors">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${classesDaCategoria(tipo?.cor)}`}
                                >
                                  {tipo?.icone && <i className={`${tipo.icone} text-[10px]`}></i>}
                                  {tipo?.nome ?? registro.tipoId}
                                </span>
                                <span className="text-xs text-foreground-500">
                                  {formatData(registro.data)}
                                </span>
                              </div>
                              {registro.custoCentavos !== null && (
                                <span className="text-xs font-semibold text-foreground-700">
                                  {(registro.custoCentavos / 100).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground-800 leading-relaxed">
                              {registro.descricao}
                            </p>
                            {(registro.prestadorNome || registro.responsavel) && (
                              <p className="text-xs text-foreground-500 mt-2">
                                <i className="ri-user-line mr-1"></i>
                                {registro.prestadorNome || registro.responsavel}
                              </p>
                            )}
                            {registro.observacoes && (
                              <p className="text-xs text-foreground-500 mt-2 pt-2 border-t border-background-200/50">
                                {registro.observacoes}
                              </p>
                            )}

                            {registro.anexos.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-background-200/50 flex flex-wrap gap-2">
                                {registro.anexos.map((anexo) => (
                                  <button
                                    key={anexo.caminho}
                                    type="button"
                                    onClick={() => void abrirAnexo(anexo)}
                                    title={anexo.nome}
                                    className="inline-flex items-center gap-2 bg-background-100 border border-background-200/70 rounded-md px-2.5 py-1.5 text-xs hover:border-background-300 hover:text-primary-600 transition-colors"
                                  >
                                    <i
                                      className={
                                        /\.(jpe?g|png|webp)$/i.test(anexo.nome)
                                          ? 'ri-image-line'
                                          : 'ri-file-text-line'
                                      }
                                    ></i>
                                    <span className="max-w-[160px] truncate">{anexo.nome}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
